import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertBarcodeForm } from '../hooks/useUpsertBarcodeForm';
import type { BarcodeDto } from '../types/BarcodeDto';
import { UpsertBarcodeForm } from './UpsertBarcodeForm';

interface NewBarcodeModalProps {
	close: () => void;
	isOpened: boolean;
	triggerListUpdate: () => void;
	skuid: string;
}

export function NewBarcodeModal({
	isOpened,
	close,
	triggerListUpdate,
	skuid,
}: NewBarcodeModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertBarcodeForm();

	const completeBarcode = useMemo(() => {
		const barcode = inputs;

		barcode.skuid = skuid;

		return barcode;
	}, [skuid, inputs]);

	const newBarcodeMutation = mutate<BarcodeDto>(
		`${organisation}/barcodes/new/${skuid}`,
		completeBarcode,
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		triggerListUpdate();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'New Barcode'} />}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={newBarcodeMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Barcode Added!'}
					body={'A new barcode has been added. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newBarcodeMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertBarcodeForm form={form} mutation={newBarcodeMutation} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
